import { useEffect, useState } from "react";
import workPolicyApi, {
  PolicyKind,
  WorkPolicyApi,
  WorkPolicyApiError,
  WorkPolicyRequest,
  WorkPolicySummary
} from "../../../api/work-policy-api";
import { PolicyCatalog } from "../../../components/work-type-management/policy-catalog";
import { WorkPolicyEditor } from "../../../components/work-type-management/work-policy-editor";
import { WorkPolicyRecoveryState } from "../../../components/work-type-management/work-policy-save-recovery";
import { useWorkPolicyCatalog } from "../../../components/work-type-management/hooks/use-work-policy-catalog";
import {
  buildWorkPolicyRequest,
  createEmptyWorkPolicyForm,
  resetWorkPolicyTypeDetails,
  useWorkPolicyForm,
  validateWorkPolicyForm,
  WorkPolicyFormErrors,
  WorkPolicyFormValue
} from "../../../components/work-type-management/hooks/use-work-policy-form";
import {
  MasterDetail,
  Screen,
  ScreenHeader
} from "../../../components/work-type-management/work-type-management.styles";

interface WorkTypeManagementScreenProps {
  api?: WorkPolicyApi;
  initialKind?: PolicyKind;
  confirmUnsaved?: (message: string) => boolean;
  confirmAction?: (message: string) => boolean;
  onSafeBack?: () => void;
  registerUnsavedGuard?: (guard: () => boolean) => void | (() => void);
}

function errorLike(error: unknown) {
  return error as WorkPolicyApiError;
}

function focusFirstError(errors: WorkPolicyFormErrors) {
  const first = Object.keys(errors)[0];
  if (!first) return;
  const direct = document.getElementById(`work-policy-${first}`);
  const nested = direct?.querySelector<HTMLElement>("input, select, button");
  (nested ?? direct)?.focus();
}

export const WorkTypeManagementScreen = ({
  api = workPolicyApi,
  initialKind = "APPROVAL",
  confirmUnsaved = message => window.confirm(message),
  confirmAction = message => window.confirm(message),
  onSafeBack = () => window.history.back(),
  registerUnsavedGuard
}: WorkTypeManagementScreenProps) => {
  const catalog = useWorkPolicyCatalog(api, initialKind);
  const form = useWorkPolicyForm();
  const [selectedId, setSelectedId] = useState<number>();
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<WorkPolicyFormErrors>({});
  const [recovery, setRecovery] = useState<WorkPolicyRecoveryState | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const canLeave = () =>
    !form.dirty ||
    confirmUnsaved("저장하지 않은 변경이 있습니다. 편집 내용을 버리고 이동할까요?");

  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!form.dirty) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [form.dirty]);

  useEffect(() => {
    if (!registerUnsavedGuard) return;
    return registerUnsavedGuard(canLeave);
  });

  useEffect(() => {
    if (Object.keys(errors).length > 0) focusFirstError(errors);
  }, [errors]);

  const clearFeedback = () => {
    setErrors({});
    setRecovery(null);
    setSuccessMessage(null);
  };

  const selectPolicy = async (
    summary: WorkPolicySummary,
    trigger: HTMLButtonElement
  ) => {
    if (!canLeave()) {
      trigger.focus();
      return;
    }
    clearFeedback();
    setSelectedId(summary.workPolicyId);
    setDetailLoading(true);
    try {
      const response = await api.detail(summary.workPolicyId);
      form.loadDetail(response.data);
    } catch (error) {
      const failure = errorLike(error);
      setRecovery({
        code: failure.code ?? "WORK_POLICY_DETAIL_FAILED",
        message:
          failure.message || "정책 상세를 불러오지 못했습니다. 목록에서 다시 선택하세요.",
        resourceId: failure.resourceId,
        lastCompletedStage: failure.lastCompletedStage,
        retryable: failure.retryable ?? false,
        fingerprint: failure.fingerprint,
        request: buildWorkPolicyRequest(
          form.value ?? {
            ...createEmptyWorkPolicyForm(summary.policyKind),
            profileName: summary.profileName,
            workType: summary.workType
          }
        )
      });
    } finally {
      setDetailLoading(false);
    }
  };

  const switchKind = (kind: PolicyKind) => {
    if (kind === catalog.policyKind) return;
    if (!canLeave()) return;
    clearFeedback();
    setSelectedId(undefined);
    form.setValue(null);
    catalog.switchKind(kind);
  };

  const startNew = () => {
    if (!canLeave()) return;
    clearFeedback();
    setSelectedId(undefined);
    form.startNew(catalog.policyKind);
  };

  const changeWorkType = (workType: WorkPolicyFormValue["workType"]) => {
    if (!form.value || form.value.workPolicyId !== undefined || form.value.workType === workType) {
      return;
    }
    const labels = {
      staggered: "시차 출퇴근",
      flexible: "탄력 근로",
      selective: "선택 근로"
    };
    if (
      !confirmAction(
        `${labels[form.value.workType]}에서 ${labels[workType]}(으)로 바꾸면 현재 유형별 상세가 제거됩니다. 계속할까요?`
      )
    ) {
      return;
    }
    clearFeedback();
    form.setValue(resetWorkPolicyTypeDetails(form.value, workType));
  };

  const reconcileNetworkUnknown = async (request: WorkPolicyRequest) => {
    const list = await api.list({ policyKind: request.policyKind });
    const candidate = list.data.find(
      summary =>
        summary.profileName === request.profileName &&
        summary.workType === request.workType
    );
    if (!candidate) {
      setRecovery({
        code: "WORK_POLICY_NETWORK_UNKNOWN",
        message:
          "목록에서 저장 결과를 확인하지 못했습니다. 새로 만들기 전에 서버 상태를 다시 확인하세요.",
        retryable: false,
        request
      });
      return;
    }
    const detail = await api.detail(candidate.workPolicyId);
    setSelectedId(candidate.workPolicyId);
    form.loadDetail(detail.data);
    setRecovery(null);
    setSuccessMessage(`서버에서 ID ${candidate.workPolicyId} 저장을 확인했습니다.`);
  };

  const save = async () => {
    if (!form.value || saving) return;
    const nextErrors = validateWorkPolicyForm(form.value, catalog.summaries);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    if (
      form.value.policyKind === "BASE" &&
      !confirmAction(
        `${form.value.profileName}의 ${form.value.effectiveStartDate} ~ ${form.value.effectiveEndDate} 미래 예정 데이터만 재생성 대상으로 저장합니다. 과거 실적은 변경하지 않습니다. 계속할까요?`
      )
    ) {
      return;
    }

    const request = buildWorkPolicyRequest(form.value);
    let persistedResourceId: number | undefined;
    setSaving(true);
    setRecovery(null);
    setSuccessMessage(null);
    try {
      const response = form.value.workPolicyId
        ? await api.update(form.value.workPolicyId, request)
        : await api.create(request);
      const resourceId = response.resourceId ?? response.data.workPolicyId;
      persistedResourceId = resourceId;
      const canonical = await api.detail(resourceId);
      setSelectedId(resourceId);
      form.loadDetail(canonical.data);
      setSuccessMessage(
        `저장 완료 · ID ${resourceId} · ${response.lastCompletedStage ?? "RECONCILED"}`
      );
      await catalog.refresh(request.policyKind);
    } catch (error) {
      const failure = errorLike(error);
      if (failure.networkUnknown || failure.code === "WORK_POLICY_NETWORK_UNKNOWN") {
        try {
          await reconcileNetworkUnknown(request);
        } catch {
          setRecovery({
            code: "WORK_POLICY_NETWORK_UNKNOWN",
            message:
              "목록과 상세 재조회로도 저장 결과를 확인하지 못했습니다. 입력값은 유지됩니다.",
            retryable: false,
            request
          });
        }
      } else {
        setRecovery({
          code: failure.code ?? "WORK_POLICY_SAVE_FAILED",
          message: failure.message || "정책을 저장하지 못했습니다.",
          resourceId: failure.resourceId ?? persistedResourceId,
          lastCompletedStage: failure.lastCompletedStage,
          retryable: failure.retryable ?? false,
          fingerprint: failure.fingerprint,
          request
        });
        if (failure.issues) {
          const serverErrors = failure.issues.reduce<WorkPolicyFormErrors>(
            (accumulator, issue) => ({
              ...accumulator,
              [issue.path]: issue.message
            }),
            {}
          );
          setErrors(serverErrors);
        }
      }
    } finally {
      setSaving(false);
    }
  };

  const retry = async () => {
    if (
      !recovery?.resourceId ||
      !recovery.lastCompletedStage ||
      !recovery.fingerprint ||
      saving
    ) {
      return;
    }
    setSaving(true);
    try {
      const response = await api.retry(recovery.resourceId, {
        expectedStage: recovery.lastCompletedStage,
        fingerprint: recovery.fingerprint,
        policy: recovery.request
      });
      setRecovery(null);
      setSelectedId(recovery.resourceId);
      if (form.value) {
        form.markSaved({ ...form.value, workPolicyId: recovery.resourceId });
      }
      setSuccessMessage(
        `복구 완료 · ID ${recovery.resourceId} · ${response.lastCompletedStage ?? "RECONCILED"}`
      );
      await catalog.refresh(recovery.request.policyKind);
    } catch (error) {
      const failure = errorLike(error);
      setRecovery(current =>
        current
          ? {
              ...current,
              code: failure.code ?? current.code,
              message: failure.message || current.message,
              lastCompletedStage:
                failure.lastCompletedStage ?? current.lastCompletedStage,
              fingerprint: failure.fingerprint ?? current.fingerprint,
              retryable: failure.retryable ?? current.retryable
            }
          : current
      );
    } finally {
      setSaving(false);
    }
  };

  const softDelete = async () => {
    if (!form.value?.workPolicyId || saving) return;
    const isBasePolicy = form.value.policyKind === "BASE";
    if (
      !confirmAction(
        `정책을 삭제할까요? 삭제 대상: ${form.value.profileName}.${
          isBasePolicy
            ? ` 대상 기간은 ${form.value.effectiveStartDate} ~ ${form.value.effectiveEndDate}입니다.`
            : ""
        } 과거 신청 snapshot과 실적은 유지됩니다.`
      )
    ) {
      return;
    }
    setSaving(true);
    try {
      await api.softDelete(form.value.workPolicyId);
      setSuccessMessage(`ID ${form.value.workPolicyId} 정책을 삭제했습니다.`);
      form.markSaved();
      await catalog.refresh(form.value.policyKind);
    } catch (error) {
      const failure = errorLike(error);
      setRecovery({
        code: failure.code ?? "WORK_POLICY_DELETE_FAILED",
        message: failure.message || "정책을 삭제하지 못했습니다.",
        resourceId: form.value.workPolicyId,
        lastCompletedStage: failure.lastCompletedStage,
        retryable: false,
        fingerprint: failure.fingerprint,
        request: buildWorkPolicyRequest(form.value)
      });
    } finally {
      setSaving(false);
    }
  };

  const searchDepartments = async (query: string) => {
    const response = await api.searchDepartments(query, 30);
    return response.data.map(department => ({
      id: String(department.departmentId),
      name: department.departmentName,
      description: department.departmentPath
    }));
  };

  const searchEmployees = async (query: string) => {
    const response = await api.searchEmployees(query, 30);
    return response.data.map(employee => ({
      id: employee.employeeId,
      name: employee.employeeName,
      description: `${employee.employeeId} · ${employee.departmentPath}`
    }));
  };

  return (
    <Screen aria-label="근무 유형 관리 workspace">
      <ScreenHeader>
        <div>
          <h1>근무 유형 관리</h1>
          <p>결재 신청용과 기본 정책을 선택하고 근무 규칙을 편집합니다.</p>
        </div>
      </ScreenHeader>
      <MasterDetail>
        <PolicyCatalog
          policyKind={catalog.policyKind}
          summaries={catalog.filteredSummaries}
          totalCount={catalog.summaries.length}
          filters={catalog.filters}
          loading={catalog.loading}
          error={catalog.error}
          filtersActive={catalog.filtersActive}
          selectedId={selectedId}
          onKindChange={switchKind}
          onFiltersChange={catalog.setFilters}
          onResetFilters={catalog.resetFilters}
          onSelect={selectPolicy}
          onCreate={startNew}
          onRetry={() => void catalog.refresh()}
          onSafeBack={onSafeBack}
        />
        <WorkPolicyEditor
          value={form.value}
          errors={errors}
          loading={detailLoading}
          saving={saving}
          dirty={form.dirty}
          recovery={recovery}
          successMessage={successMessage}
          onChange={(field, value) => {
            clearFeedback();
            form.setField(field, value);
          }}
          onWorkTypeChange={changeWorkType}
          onSave={save}
          onDelete={softDelete}
          onRetry={retry}
          searchDepartments={searchDepartments}
          searchEmployees={searchEmployees}
          confirmTargetChange={confirmAction}
          confirmDetailChange={confirmAction}
        />
      </MasterDetail>
    </Screen>
  );
};

export default WorkTypeManagementScreen;
